import React, { useCallback, useState, useRef } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  Tooltip,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { PreventivoRow, Candidate } from "../../services/preventiviApi";
import { MatchPickerCell } from "./MatchPickerCell";
import { getEffectiveMatch, getRowMetadata, isUdmMismatch } from "../../utils/matchSelection";
import { isNpRow } from "../../utils/npCodes";
import { formatFixed2, formatMax2, parseDecimalInput } from "../../utils/numberFormat";

interface RowsTableProps {
  rows: PreventivoRow[];
  readonly: boolean;
  onReorder: (orderedIds: string[]) => void;
  onUpdateRow: (rowId: string, data: Partial<PreventivoRow>) => void;
  onDeleteRow?: (rowId: string) => void;
  onAddSection?: (afterPosition: number, disciplina?: "ELE" | "MEC") => void;
  onInsertAfter?: (afterRowId: string, type: "item" | "section") => void;
  preventivoCategoria?: string | null;
  preventivoId?: string;
  onRematchStarted?: () => void;
}

const ROW_RENDER_BATCH = 40;

// ── Layout ────────────────────────────────────────────────────────────────────
// Fixed table layout: the narrow columns keep exactly these widths (padding
// included) and Descrizione estesa + Match share whatever is left.
const COL_W = {
  drag: 24,
  capitolo: 40,
  codice: 86,
  breve: "15%",
  qty: 66,
  um: 46,
  mat: 64,
  posa: 52,
  mano: 64,
  pv: 66,
  totale: 76,
  actions: 54,
} as const;
const TABLE_MIN_W = 1080;

const CELL_BORDER = "1px solid rgba(0,0,0,0.06)";
const FONT_TEXT = "0.76rem";
const FONT_NUM = "0.74rem";
const FONT_CAPTION = "0.68rem";

interface SortableRowProps {
  row: PreventivoRow;
  index: number;
  readonly: boolean;
  onUpdate: (rowId: string, data: Partial<PreventivoRow>) => void;
  onDelete?: (rowId: string) => void;
  onInsertAfter?: (afterRowId: string, type: "item" | "section") => void;
  preventivoCategoria?: string | null;
  preventivoId?: string;
  onRematchStarted?: () => void;
}

// ── Description expand dialog ─────────────────────────────────────────────────

const DescExpandDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  index: number;
  code?: string | null;
  text: string;
}> = ({ open, onClose, index, code, text }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
    <DialogTitle sx={{ fontWeight: 600, fontSize: "0.95rem", pb: 1 }}>
      Descrizione — Voce {index + 1}{code ? ` (${code})` : ""}
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" sx={{ fontSize: "0.85rem", whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
        {text}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 2.5, py: 1.5 }}>
      <Button onClick={onClose} size="small">Chiudi</Button>
    </DialogActions>
  </Dialog>
);

// ── Numeric input: shows a rounded value, commits on blur/Enter ───────────────

const NumberInput: React.FC<{
  value: unknown;
  format: (v: unknown) => string;
  onCommit: (n: number | null) => void;
  placeholder?: string;
  color?: string;
}> = ({ value, format, onCommit, placeholder, color }) => {
  const focused = useRef(false);
  const shown = format(value);
  const [local, setLocal] = useState(shown);
  if (!focused.current && local !== shown) setLocal(shown);

  return (
    <TextField
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onFocus={(e) => { focused.current = true; e.target.select(); }}
      onBlur={() => {
        focused.current = false;
        // Untouched fields must not overwrite the precise stored value with the rounded one.
        if (local === shown) return;
        onCommit(parseDecimalInput(local));
      }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
      size="small"
      variant="standard"
      placeholder={placeholder}
      inputProps={{ inputMode: "decimal", style: { textAlign: "right", padding: "2px 0" } }}
      InputProps={{
        disableUnderline: true,
        sx: {
          fontSize: FONT_NUM,
          fontVariantNumeric: "tabular-nums",
          color: color ?? "text.primary",
          borderRadius: "4px",
          px: 0.25,
          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
          "&.Mui-focused": { bgcolor: "rgba(21,101,192,0.06)" },
        },
      }}
      sx={{ width: "100%" }}
    />
  );
};

// ── Insert-after menu ─────────────────────────────────────────────────────────

const InsertMenu: React.FC<{
  rowId: string;
  onInsertAfter?: (afterRowId: string, type: "item" | "section") => void;
}> = ({ rowId, onInsertAfter }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  if (!onInsertAfter) return null;
  return (
    <>
      <Tooltip title="Inserisci sotto">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
          sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "primary.main" } }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: "0.95rem" }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: "0.75rem", minWidth: 180 } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          dense
          onClick={() => { onInsertAfter(rowId, "item"); setAnchor(null); }}
        >
          <ListItemIcon><PlaylistAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Nuova riga sotto" />
        </MenuItem>
        <MenuItem
          dense
          onClick={() => { onInsertAfter(rowId, "section"); setAnchor(null); }}
        >
          <ListItemIcon><LibraryAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Nuovo capitolo sotto" />
        </MenuItem>
      </Menu>
    </>
  );
};

const RowActions: React.FC<{
  rowId: string;
  deleteLabel: string;
  onDelete?: (rowId: string) => void;
  onInsertAfter?: (afterRowId: string, type: "item" | "section") => void;
}> = ({ rowId, deleteLabel, onDelete, onInsertAfter }) => (
  <Box sx={{ display: "flex", gap: 0.25, justifyContent: "center" }}>
    <InsertMenu rowId={rowId} onInsertAfter={onInsertAfter} />
    <Tooltip title={deleteLabel}>
      <IconButton
        size="small"
        onClick={() => onDelete?.(rowId)}
        sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "error.main" } }}
      >
        <DeleteOutlineIcon sx={{ fontSize: "0.95rem" }} />
      </IconButton>
    </Tooltip>
  </Box>
);

// ── Section row ───────────────────────────────────────────────────────────────

const SectionRow: React.FC<{
  row: PreventivoRow;
  readonly: boolean;
  onUpdate: (rowId: string, data: Partial<PreventivoRow>) => void;
  onDelete?: (rowId: string) => void;
  onInsertAfter?: (afterRowId: string, type: "item" | "section") => void;
  colCount: number;
}> = ({ row, readonly, onUpdate, onDelete, onInsertAfter, colCount }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const indent = (row.level || 0) * 16;
  const titleFocused = useRef(false);
  const numeroFocused = useRef(false);
  const [localTitle, setLocalTitle] = useState(row.query_text);
  const [localNumero, setLocalNumero] = useState(row.numero ?? "");
  if (!titleFocused.current && localTitle !== row.query_text) setLocalTitle(row.query_text);
  if (!numeroFocused.current && localNumero !== (row.numero ?? "")) setLocalNumero(row.numero ?? "");

  // drag + capitolo + actions (when editable) leave the rest for the title
  const titleColSpan = colCount - 1 - (readonly ? 0 : 2);

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{ bgcolor: "#E8EAF6", "&:hover": { bgcolor: "#D3D7EF" } }}
    >
      {!readonly && (
        <TableCell sx={{ py: 0.25, borderRight: CELL_BORDER }}>
          <Box {...attributes} {...listeners} sx={{ cursor: "grab", color: "#7986CB", display: "flex" }}>
            <DragIndicatorIcon sx={{ fontSize: "0.95rem" }} />
          </Box>
        </TableCell>
      )}
      {/* Capitolo — editable chapter number (1, 1.1, 2, …) */}
      <TableCell sx={{ py: 0.25, borderRight: CELL_BORDER }}>
        {readonly ? (
          <Typography sx={{ fontFamily: "monospace", color: "#283593", fontWeight: 700, fontSize: FONT_TEXT, textAlign: "center" }}>
            {row.numero || ""}
          </Typography>
        ) : (
          <TextField
            value={localNumero}
            onChange={(e) => setLocalNumero(e.target.value)}
            onFocus={() => { numeroFocused.current = true; }}
            onBlur={() => {
              numeroFocused.current = false;
              const next = localNumero.trim();
              if (next !== (row.numero ?? "")) onUpdate(row.id, { numero: next });
            }}
            size="small"
            variant="standard"
            placeholder="#"
            inputProps={{ style: { textAlign: "center", padding: "2px 0" } }}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: FONT_TEXT, fontFamily: "monospace", fontWeight: 700, color: "#283593" },
            }}
            sx={{ width: "100%" }}
          />
        )}
      </TableCell>
      <TableCell colSpan={titleColSpan} sx={{ py: 0.4, pl: 1 + indent / 8 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {readonly ? (
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#283593", fontSize: "0.8rem" }}>
              {row.query_text}
            </Typography>
          ) : (
            <TextField
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onFocus={() => { titleFocused.current = true; }}
              onBlur={() => {
                titleFocused.current = false;
                if (localTitle !== row.query_text) onUpdate(row.id, { query_text: localTitle });
              }}
              size="small"
              variant="standard"
              inputProps={{ style: { padding: "2px 0" } }}
              InputProps={{ disableUnderline: true, sx: { fontSize: "0.8rem", fontWeight: 700, color: "#283593" } }}
              sx={{ flex: 1 }}
            />
          )}
          {row.disciplina && (
            <Chip
              label={row.disciplina}
              size="small"
              sx={{
                bgcolor: row.disciplina === "ELE" ? "#E3F2FD" : "#FFF3E0",
                color: row.disciplina === "ELE" ? "#1565C0" : "#E65100",
                fontWeight: 700, fontSize: "0.62rem", height: 16,
              }}
            />
          )}
        </Box>
      </TableCell>
      {!readonly && (
        <TableCell sx={{ py: 0.25 }}>
          <RowActions rowId={row.id} deleteLabel="Elimina sezione" onDelete={onDelete} onInsertAfter={onInsertAfter} />
        </TableCell>
      )}
    </TableRow>
  );
};

// ── Item row ──────────────────────────────────────────────────────────────────

const CostValue: React.FC<{ value: unknown; color: string }> = ({ value, color }) => {
  const s = formatFixed2(value);
  return (
    <Typography
      sx={{
        color: s ? color : "text.disabled",
        fontWeight: 600,
        fontSize: FONT_NUM,
        fontVariantNumeric: "tabular-nums",
        textAlign: "right",
        lineHeight: 1.6,
      }}
    >
      {s || "—"}
    </Typography>
  );
};

const SortableRow: React.FC<SortableRowProps> = ({
  row, index, readonly, onUpdate, onDelete, onInsertAfter, preventivoCategoria, preventivoId, onRematchStarted,
}) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: row.id });
  const [descOpen, setDescOpen] = useState(false);

  // Local state buffers — flushed to parent only on blur to avoid per-keystroke re-renders
  const descFocused = useRef(false);
  const codeFocused = useRef(false);
  const breveFocused = useRef(false);
  const [localDesc, setLocalDesc] = useState(row.query_text);
  const [localCode, setLocalCode] = useState(row.code ?? "");
  const [localBreve, setLocalBreve] = useState(row.descrizione_breve ?? "");
  if (!descFocused.current && localDesc !== row.query_text) setLocalDesc(row.query_text);
  if (!codeFocused.current && localCode !== (row.code ?? "")) setLocalCode(row.code ?? "");
  if (!breveFocused.current && localBreve !== (row.descrizione_breve ?? "")) setLocalBreve(row.descrizione_breve ?? "");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : "auto",
  };

  const meta = row.overridden_metadata ?? {};
  const topCandidate = getEffectiveMatch(row);
  const rowMeta = getRowMetadata(row);
  const isNp = isNpRow(row);

  const getValue = (key: string) => rowMeta[key];

  const prezzo = getValue("prezzo");
  const prezzoNum = prezzo != null ? parseFloat(String(prezzo)) : null;
  const subtotal = prezzoNum != null && !isNaN(prezzoNum) ? formatFixed2(prezzoNum * Number(row.quantity)) : "";

  const costoMat = getValue("costo_mat_unitario");
  const tempoPosa = getValue("tempo_posa_ore");
  const costoMano = getValue("costo_mano_unitario");

  // Descrizione breve of the *matched catalog item* (shown as a caption under
  // the extended description) — distinct from the row's own descrizione_breve
  // (extracted from the source PDF), which now has its own dedicated column.
  const matchBreve = getValue("descrizione_breve");
  const rowUnit = row.unit;
  const matchUdm = getValue("udm");
  const udmMismatch = isUdmMismatch(rowUnit, matchUdm);

  const handleChoose = (candidate: Candidate, mergedCandidates: Candidate[]) => {
    onUpdate(row.id, {
      selected_item_uid: candidate.uid,
      confirmed: true,
      // Mark feedback as handled so the close-time deferred flush skips this
      // row and avoids sending a duplicate positive signal.
      feedback_sent: true,
      candidates: mergedCandidates,
      // Manual fix resolves the pipeline error state (backend clears it too)
      match_error: null,
    });
  };

  const handleDeselect = () => {
    onUpdate(row.id, {
      selected_item_uid: null,
      confirmed: false,
      feedback_sent: false,
      // Clear manually_selected flag from all candidates
      candidates: (row.candidates ?? []).map((c) =>
        c.manually_selected ? { ...c, manually_selected: false } : c
      ),
    });
  };

  const textInputSx = { fontSize: FONT_TEXT, lineHeight: 1.45 };

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        sx={{
          verticalAlign: "top",
          "&:hover": { bgcolor: isNp ? "#FFF9C4" : "rgba(0,0,0,0.02)" },
          bgcolor: isDragging ? "action.selected" : isNp ? "#FFFDE7" : "inherit",
        }}
      >
        {/* Drag handle */}
        {!readonly && (
          <TableCell sx={{ borderRight: CELL_BORDER }}>
            <Box {...attributes} {...listeners} sx={{ cursor: "grab", color: "text.disabled", "&:active": { cursor: "grabbing" }, display: "flex", pt: 0.25 }}>
              <DragIndicatorIcon sx={{ fontSize: "0.95rem" }} />
            </Box>
          </TableCell>
        )}

        {/* Capitolo — Excel IMPORT value: chapter number or 'a' for articles */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          <Typography
            sx={{
              fontFamily: "monospace",
              color: "text.secondary",
              fontWeight: 600,
              fontSize: FONT_CAPTION,
              textAlign: "center",
              lineHeight: 1.8,
              wordBreak: "break-all",
            }}
          >
            {row.numero || "a"}
          </Typography>
        </TableCell>

        {/* Codice */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          {readonly ? (
            <Typography sx={{ fontSize: FONT_CAPTION, fontFamily: "monospace", wordBreak: "break-all", lineHeight: 1.6 }}>
              {row.code ?? ""}
            </Typography>
          ) : (
            <TextField
              value={localCode}
              onChange={(e) => setLocalCode(e.target.value)}
              onFocus={() => { codeFocused.current = true; }}
              onBlur={() => {
                codeFocused.current = false;
                const next = localCode || undefined;
                if (next !== row.code) onUpdate(row.id, { code: next });
              }}
              size="small"
              variant="standard"
              multiline
              maxRows={3}
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: FONT_CAPTION, fontFamily: "monospace", lineHeight: 1.5, p: 0, "& textarea": { wordBreak: "break-all" } },
              }}
              sx={{ width: "100%" }}
            />
          )}
        </TableCell>

        {/* Descrizione breve (own field, extracted from the source file) */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          {readonly ? (
            <Typography
              sx={{ ...textInputSx, color: "text.secondary", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}
            >
              {row.descrizione_breve || "—"}
            </Typography>
          ) : (
            <TextField
              value={localBreve}
              onChange={(e) => setLocalBreve(e.target.value)}
              onFocus={() => { breveFocused.current = true; }}
              onBlur={() => {
                breveFocused.current = false;
                const next = localBreve || undefined;
                if (next !== (row.descrizione_breve ?? undefined)) onUpdate(row.id, { descrizione_breve: next });
              }}
              size="small"
              variant="standard"
              multiline
              maxRows={3}
              InputProps={{ disableUnderline: true, sx: { ...textInputSx, color: "text.secondary", p: 0 } }}
              sx={{ width: "100%" }}
            />
          )}
        </TableCell>

        {/* Descrizione estesa */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {readonly ? (
                <Typography
                  sx={{ ...textInputSx, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", cursor: "pointer", "&:hover": { color: "primary.main" } }}
                  onClick={() => setDescOpen(true)}
                >
                  {row.query_text}
                </Typography>
              ) : (
                <TextField
                  value={localDesc}
                  onChange={(e) => setLocalDesc(e.target.value)}
                  onFocus={() => { descFocused.current = true; }}
                  onBlur={() => {
                    descFocused.current = false;
                    if (localDesc !== row.query_text) onUpdate(row.id, { query_text: localDesc });
                  }}
                  size="small"
                  variant="standard"
                  multiline
                  maxRows={4}
                  InputProps={{ disableUnderline: true, sx: { ...textInputSx, p: 0 } }}
                  sx={{ width: "100%" }}
                />
              )}
              {matchBreve && (
                <Tooltip title="Descrizione breve dell'articolo matchato dallo storico (non è un riassunto dell'intervento)">
                  <Typography
                    sx={{ fontSize: "0.66rem", color: "text.disabled", display: "block", mt: 0.25, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                  >
                    Match: {String(matchBreve)}
                  </Typography>
                </Tooltip>
              )}
            </Box>
            <Tooltip title="Descrizione completa" placement="top" enterDelay={400}>
              <IconButton size="small" onClick={() => setDescOpen(true)} sx={{ p: 0.25, flexShrink: 0, color: "text.disabled", "&:hover": { color: "primary.main" } }}>
                <OpenInFullIcon sx={{ fontSize: "0.75rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>

        {/* Qty */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          {readonly ? (
            <Typography sx={{ fontSize: FONT_NUM, textAlign: "right", fontVariantNumeric: "tabular-nums", lineHeight: 1.6 }}>
              {formatMax2(row.quantity)}
            </Typography>
          ) : (
            <NumberInput
              value={row.quantity}
              format={formatMax2}
              onCommit={(n) => { if (n != null) onUpdate(row.id, { quantity: n || 1 }); }}
            />
          )}
        </TableCell>

        {/* UM */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: FONT_CAPTION, color: "text.secondary", lineHeight: 1.8 }}>{row.unit}</Typography>
            {udmMismatch && (
              <Tooltip title={`UM del preventivo (${row.unit}) diversa da quella dell'articolo matchato (${matchUdm})`}>
                <WarningAmberIcon sx={{ fontSize: "0.8rem", color: "#F57C00", flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>

        {/* ── 3 Cost columns — no units in values (shown in headers) ── */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          <CostValue value={costoMat} color="#1565C0" />
        </TableCell>
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          <CostValue value={tempoPosa} color="#2E7D32" />
        </TableCell>
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          <CostValue value={costoMano} color="#E65100" />
        </TableCell>

        {/* Match */}
        <TableCell sx={{ borderRight: CELL_BORDER, px: 0.25 }}>
          <MatchPickerCell
            candidates={row.candidates}
            selectedUid={row.selected_item_uid}
            queryText={row.query_text}
            readonly={readonly}
            onSelectCandidate={handleChoose}
            onDeselectCandidate={handleDeselect}
            preventivoCategoria={preventivoCategoria}
            disciplina={row.disciplina ?? undefined}
            preventivoId={preventivoId}
            rowId={row.id}
            onRematchStarted={onRematchStarted}
            matchError={row.match_error}
            isNp={isNp}
          />
        </TableCell>

        {/* Prezzo unit. (secondary) */}
        <TableCell sx={{ borderRight: CELL_BORDER }}>
          {!readonly ? (
            <Tooltip title={meta.prezzo != null ? "Prezzo modificato manualmente" : "Prezzo dal match"} placement="top" enterDelay={600}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                {meta.prezzo != null && <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />}
                <NumberInput
                  value={meta.prezzo ?? topCandidate?.metadata?.prezzo}
                  format={formatFixed2}
                  color="text.secondary"
                  onCommit={(n) => onUpdate(row.id, { overridden_metadata: { ...meta, prezzo: n ?? undefined } })}
                />
              </Box>
            </Tooltip>
          ) : (
            <Typography sx={{ fontSize: FONT_NUM, color: "text.secondary", textAlign: "right", fontVariantNumeric: "tabular-nums", lineHeight: 1.6 }}>
              {formatFixed2(prezzoNum) || "—"}
            </Typography>
          )}
        </TableCell>

        {/* Subtotale (secondary, rightmost) */}
        <TableCell sx={{ borderRight: readonly ? undefined : CELL_BORDER }}>
          <Typography sx={{ fontSize: FONT_NUM, fontWeight: subtotal ? 600 : 400, color: subtotal ? "text.secondary" : "text.disabled", textAlign: "right", fontVariantNumeric: "tabular-nums", lineHeight: 1.6 }}>
            {subtotal || "—"}
          </Typography>
        </TableCell>

        {/* Insert / Delete */}
        {!readonly && (
          <TableCell>
            <RowActions rowId={row.id} deleteLabel="Elimina riga" onDelete={onDelete} onInsertAfter={onInsertAfter} />
          </TableCell>
        )}
      </TableRow>

      <DescExpandDialog open={descOpen} onClose={() => setDescOpen(false)} index={index} code={row.code} text={row.query_text} />
    </>
  );
};

// ── Table ─────────────────────────────────────────────────────────────────────

// Total number of columns (for colSpan on section rows)
const COL_COUNT_EDIT = 14; // drag + 12 data columns + actions
const COL_COUNT_READONLY = 12;

export const RowsTable: React.FC<RowsTableProps> = ({
  rows,
  readonly,
  onReorder,
  onUpdateRow,
  onDeleteRow,
  onInsertAfter,
  preventivoCategoria,
  preventivoId,
  onRematchStarted,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [visibleCount, setVisibleCount] = useState(ROW_RENDER_BATCH);
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setVisibleCount(ROW_RENDER_BATCH);
  }, [rows.length]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240) {
      setVisibleCount((c) => Math.min(rows.length, c + ROW_RENDER_BATCH));
    }
  }, [rows.length]);

  const visibleRows = rows.slice(0, visibleCount);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    const reordered = arrayMove(rows, oldIndex, newIndex);
    onReorder(reordered.map((r) => r.id));
  }, [rows, onReorder]);

  const colCount = readonly ? COL_COUNT_READONLY : COL_COUNT_EDIT;

  const HEADER_BG = "#37474F";

  const headerCell = (label: string, width?: number | string, extra?: any, title?: string) => (
    <TableCell
      title={title}
      sx={{
        bgcolor: HEADER_BG,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.68rem",
        letterSpacing: 0.1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        py: 0.75,
        borderRight: "1px solid rgba(255,255,255,0.12)",
        ...(width != null ? { width } : {}),
        ...extra,
      }}
    >
      {label}
    </TableCell>
  );
  const numHeader = { textAlign: "right" };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <TableContainer ref={containerRef} onScroll={handleScroll} sx={{ flexGrow: 1, overflow: "auto" }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            minWidth: TABLE_MIN_W,
            "& .MuiTableCell-root": { px: 0.75, py: 0.5 },
            "& .MuiTableCell-head": { py: 0.75 },
          }}
        >
          <TableHead>
            <TableRow>
              {!readonly && headerCell("", COL_W.drag)}
              {headerCell("Cap.", COL_W.capitolo, { textAlign: "center" }, "Capitolo")}
              {headerCell("Codice", COL_W.codice)}
              {headerCell("Descrizione breve", COL_W.breve)}
              {headerCell("Descrizione estesa")}
              {headerCell("Qty", COL_W.qty, numHeader, "Quantità")}
              {headerCell("UM", COL_W.um, undefined, "Unità di misura")}
              {/* Cost columns — colour-coded text, unified header bg */}
              {headerCell("Mat. €/u", COL_W.mat, { ...numHeader, color: "#90CAF9" }, "Costo materiale unitario (€)")}
              {headerCell("Posa h", COL_W.posa, { ...numHeader, color: "#A5D6A7" }, "Tempo di posa unitario (ore)")}
              {headerCell("Man. €/u", COL_W.mano, { ...numHeader, color: "#FFAB91" }, "Costo manodopera unitario (€)")}
              {headerCell("Match")}
              {headerCell("P.V. €/u", COL_W.pv, { ...numHeader, color: "rgba(255,255,255,0.65)" }, "Prezzo di vendita unitario (€)")}
              {headerCell("Totale €", COL_W.totale, { ...numHeader, color: "rgba(255,255,255,0.65)" })}
              {!readonly && headerCell("", COL_W.actions)}
            </TableRow>
          </TableHead>
          <TableBody>
            <SortableContext items={visibleRows.map((r) => r.id)} strategy={verticalListSortingStrategy} disabled={readonly}>
              {visibleRows.map((row, idx) =>
                (row.row_type ?? "item") === "section" ? (
                  <SectionRow
                    key={row.id}
                    row={row}
                    readonly={readonly}
                    onUpdate={onUpdateRow}
                    onDelete={onDeleteRow}
                    onInsertAfter={onInsertAfter}
                    colCount={colCount}
                  />
                ) : (
                  <SortableRow
                    key={row.id}
                    row={row}
                    index={idx}
                    readonly={readonly}
                    onUpdate={onUpdateRow}
                    onDelete={onDeleteRow}
                    onInsertAfter={onInsertAfter}
                    preventivoCategoria={preventivoCategoria}
                    preventivoId={preventivoId}
                    onRematchStarted={onRematchStarted}
                  />
                )
              )}
            </SortableContext>
          </TableBody>
        </Table>
      </TableContainer>
    </DndContext>
  );
};
